import { useState } from "react";

import { differenceInMinutes } from "date-fns";

import oneOneMeeting from "@/../../hypetrain/static/img/program//one-one-meeting-icon.svg";
import actionItemsIcon from "@/../../hypetrain/static/img/program/action-items.svg";
import aiRoleplayIcon from "@/../../hypetrain/static/img/program/ai-roleplay.svg";
import announcementIcon from "@/../../hypetrain/static/img/program/announcement.svg";
import articleIcon from "@/../../hypetrain/static/img/program/article-icon.svg";
import cameraIcon from "@/../../hypetrain/static/img/program/meeting.svg";
import surveyIcon from "@/../../hypetrain/static/img/program/survey.svg";
import videoIcon from "@/../../hypetrain/static/img/program/video-icon.svg";
import { EmbedSvg } from "@/components/helpers/ui/EmbedSvg";
import { ComponentContextButton } from "@/components/pages_logged_in/program/components/ComponentContextButton";
import {
  canEndEarlyComponent,
  canGrantExtension,
} from "@/components/pages_logged_in/program/components/componentMetadata";
import { GrantExtensionDialogs } from "@/components/pages_logged_in/program/components/GrantExtensionDialogs";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  ComponentStatus,
  ComponentTypeEnum,
  GetParticipantSessionResponseQuery,
  ProgramStatus,
} from "@/graphql";
import { cn } from "@/lib/utils";
import {
  ComponentDialog,
  isLeadComponent,
  LeadComponent,
  MainComponent,
  ParticipantComponent,
} from "@/types/programs";
import { formatDate } from "@/utils/datetime";
import { NonNullableArrayItem } from "@/utils/types";

import { ProgramComponentStatus } from "./ProgramComponentStatus";

export const getIcon = (type: ComponentTypeEnum) => {
  switch (type) {
    case ComponentTypeEnum.Survey:
      return surveyIcon;
    case ComponentTypeEnum.ActionItem:
      return actionItemsIcon;
    case ComponentTypeEnum.Announcement:
      return announcementIcon;
    case ComponentTypeEnum.AiRoleplay:
      return aiRoleplayIcon;
    case ComponentTypeEnum.Video:
      return videoIcon;
    case ComponentTypeEnum.Article:
      return articleIcon;
    case ComponentTypeEnum.GroupMeeting:
      return cameraIcon;
    case ComponentTypeEnum.OneOneCoachingMaster:
    case ComponentTypeEnum.OneOneCoachingIntro:
    case ComponentTypeEnum.OneOneCoachingSession:
      return oneOneMeeting;
    default:
      return null;
  }
};

type ResponseComponentType = NonNullableArrayItem<
  NonNullable<
    GetParticipantSessionResponseQuery["participantSessionResponse"]
  >["programComponents"]
>;

type BaseProgramComponent =
  | LeadComponent
  | MainComponent
  | ParticipantComponent;

type ProgramComponentType = (BaseProgramComponent | ResponseComponentType) & {
  parentComponent?: ParticipantComponent["parentComponent"];
  targetDueDate?: ParticipantComponent["targetDueDate"];
  closedAt?: ParticipantComponent["closedAt"];
  specialty?: ParticipantComponent["specialty"];
  optional?: ParticipantComponent["optional"];
  participantSubmitAt?: ParticipantComponent["participantSubmitAt"];
};

interface ProgramComponentHeaderProps {
  component: ProgramComponentType;
  isEditable: boolean;
  onEdit?: (component: BaseProgramComponent) => void;
  onDelete?: (component: BaseProgramComponent) => void;
  onClose?: (component: BaseProgramComponent) => void;
  view?: "list" | "details" | "feed";
  showIcon?: boolean;
  showDateTime?: boolean;
  isParticipant?: boolean;
  isViewDetails?: boolean;
  program?: {
    status: ProgramStatus;
  };
}

interface ComponentHeaderTitleProps {
  showIcon?: boolean;
  component: ProgramComponentType;
  name: string;
  type: ComponentTypeEnum;
  statusView: JSX.Element;
  dateView?: JSX.Element;
  isParticipant?: boolean;
  isViewDetails?: boolean;
}

interface ProgramComponentDateInformationProps {
  targetStartDate?: string | null;
  targetDueDate?: string | null;
  targetEndDate?: string | null;
  closedAt?: string | null;
  participantSubmitAt?: string | null;
  status: ComponentStatus;
  type: ComponentTypeEnum;
  view: "lead" | "participant";
}

const getDateInformation = ({
  status,
  targetStartDate,
  targetDueDate,
  targetEndDate,
  closedAt,
  participantSubmitAt,
  type,
  view,
}: {
  status: ComponentStatus;
  targetStartDate?: string | null;
  targetDueDate?: string | null;
  targetEndDate?: string | null;
  closedAt?: string | null;
  participantSubmitAt?: string | null;
  type: ComponentTypeEnum;
  view: "lead" | "participant";
}) => {
  const isIncompletable = [
    ComponentTypeEnum.Announcement,
    ComponentTypeEnum.GroupMeeting,
  ].includes(type);

  if (isIncompletable) {
    return {
      datePrefix: undefined,
      relevantDate: targetStartDate,
      tooltipContent: (
        <div className="text-sm text-neutral-300">
          <span className="font-semibold text-white">Start: </span>
          {formatDate(targetStartDate, "MMM d, yyyy h:mm a z")}
        </div>
      ),
      className: "text-subtext",
    };
  }

  const dueDate = targetDueDate || targetEndDate;
  const startDueTooltipContent = targetStartDate && (
    <div className="text-sm">
      <div className="text-neutral-300">
        <span className="font-semibold text-white">Start: </span>
        {formatDate(targetStartDate, "MMM d, yyyy h:mm a z")}
      </div>
      {dueDate && (
        <div className="text-neutral-300">
          <span className="font-semibold text-white">Due: </span>
          {formatDate(dueDate, "MMM d, yyyy h:mm a z")}
        </div>
      )}
      {view === "lead" && (
        <div className="text-neutral-300">
          <span className="font-semibold text-white">End: </span>
          {formatDate(targetEndDate, "MMM d, yyyy h:mm a z")}
        </div>
      )}
    </div>
  );
  const startEndTooltipContent = targetStartDate && (
    <div className="text-sm">
      <div className="text-neutral-300">
        <span className="font-semibold text-white">Start: </span>
        {formatDate(targetStartDate, "MMM d, yyyy h:mm a z")}
      </div>
      {(closedAt || dueDate) && (
        <div className="text-neutral-300">
          <span className="font-semibold text-white">End: </span>
          {formatDate(closedAt || dueDate, "MMM d, yyyy h:mm a z")}
        </div>
      )}
    </div>
  );

  return {
    [ComponentStatus.Soon]: {
      datePrefix: "Starts",
      relevantDate: targetStartDate,
      tooltipContent: startDueTooltipContent,
      className: "text-subtext",
    },
    [ComponentStatus.Upcoming]: {
      datePrefix: "Starts",
      relevantDate: targetStartDate,
      tooltipContent: startDueTooltipContent,
      className: "text-subtext",
    },
    [ComponentStatus.Ongoing]: {
      datePrefix: "Due",
      relevantDate: dueDate,
      tooltipContent: startDueTooltipContent,
      className: "text-subtext",
    },
    [ComponentStatus.Completed]: {
      datePrefix: "Completed",
      relevantDate: participantSubmitAt || dueDate,
      tooltipContent: startDueTooltipContent,
      className: "text-subtext",
    },
    [ComponentStatus.PastDue]: {
      datePrefix: "Due",
      relevantDate: dueDate,
      tooltipContent: startDueTooltipContent,
      className: "text-yellow-800",
    },
    [ComponentStatus.DidNotFinish]: {
      datePrefix: "Closed",
      relevantDate: closedAt,
      tooltipContent: startEndTooltipContent,
      className: "text-subtext",
    },
    [ComponentStatus.DidNotPass]: {
      datePrefix: "Closed",
      relevantDate: closedAt,
      tooltipContent: startEndTooltipContent,
      className: "text-subtext",
    },
    [ComponentStatus.InDesign]: {
      datePrefix: "Starts",
      relevantDate: targetStartDate,
      tooltipContent: startDueTooltipContent,
      className: "text-subtext",
    },
    [ComponentStatus.Missed]: {
      datePrefix: "Closed",
      relevantDate: closedAt,
      tooltipContent: startEndTooltipContent,
      className: "text-subtext",
    },
    [ComponentStatus.NoResponse]: {
      datePrefix: "Closed",
      relevantDate: closedAt,
      tooltipContent: startEndTooltipContent,
      className: "text-subtext",
    },
    [ComponentStatus.NotStarted]: {
      datePrefix: "Starts",
      relevantDate: targetStartDate,
      tooltipContent: startDueTooltipContent,
      className: "text-subtext",
    },
    [ComponentStatus.Started]: {
      datePrefix: "Due",
      relevantDate: dueDate,
      tooltipContent: startDueTooltipContent,
      className: "text-subtext",
    },
  }[status];
};

function ProgramComponentDateInfo({
  targetStartDate,
  targetDueDate,
  targetEndDate,
  closedAt,
  participantSubmitAt,
  status,
  type,
  view,
}: ProgramComponentDateInformationProps) {
  const { datePrefix, relevantDate, tooltipContent, className } =
    getDateInformation({
      status,
      targetStartDate,
      targetDueDate,
      targetEndDate,
      closedAt,
      participantSubmitAt,
      type,
      view,
    });

  if (relevantDate) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div className={className}>
            {datePrefix
              ? `${datePrefix} ${formatDate(relevantDate, "MMMM d")}`
              : formatDate(relevantDate, "MMMM d")}
          </div>
        </TooltipTrigger>
        <TooltipContent side="right">{tooltipContent}</TooltipContent>
      </Tooltip>
    );
  }

  return null;
}

function ComponentHeaderTitle({
  showIcon,
  component,
  name,
  type,
  statusView,
  dateView,
  isViewDetails,
}: ComponentHeaderTitleProps) {
  const componentSpecialty =
    component.specialty || component.parentComponent?.specialty;

  const isOneOneCoachingComponent = [
    ComponentTypeEnum.OneOneCoachingMaster,
    ComponentTypeEnum.OneOneCoachingIntro,
    ComponentTypeEnum.OneOneCoachingSession,
  ].includes(type);

  const specialtyView = showIcon &&
    isOneOneCoachingComponent &&
    componentSpecialty && (
      <div className="xs:text-sm text-subtext font-normal md:text-base">
        {componentSpecialty?.name}
      </div>
    );

  const isGroupMeeting = type === ComponentTypeEnum.GroupMeeting;
  const meetingDuration = isGroupMeeting &&
    component.targetStartDate &&
    component.targetEndDate && (
      <div className="xs:text-sm text-subtext font-normal md:text-base">
        {differenceInMinutes(
          new Date(component.targetEndDate),
          new Date(component.targetStartDate),
        )}{" "}
        mins
      </div>
    );
  const isOptional =
    component.optional ||
    [ComponentTypeEnum.Announcement, ComponentTypeEnum.GroupMeeting].includes(
      type,
    );

  const content = (
    <div
      className={cn(
        "xs:gap-px xs:items-start w-full flex flex-1 md:items-center md:gap-4",
        {
          "xs:flex-col md:flex-row": !isViewDetails,
        },
      )}
    >
      <div className="xs:gap-px flex flex-1 flex-col md:gap-0.5">
        <div
          className={cn(
            "font-semibold",
            isViewDetails ? "text-2xl" : "text-lg",
          )}
        >
          {name}
        </div>
        <div className="flex flex-wrap text-subtext xs:text-sm font-normal md:text-base *:not-first:before:content-['\a0\a0•\a0\a0']">
          {isViewDetails ? (
            <>
              {statusView}
              {isOptional && <div>Optional</div>}
              {specialtyView}
              {meetingDuration}
            </>
          ) : (
            <>
              {isOptional && <div>Optional</div>}
              {dateView}
              {specialtyView}
              {meetingDuration}
            </>
          )}
        </div>
        {!isViewDetails && (
          <div className="xs:block md:hidden">{statusView}</div>
        )}
      </div>
    </div>
  );

  return (
    <div className="flex xs:gap-3.5 xs:items-start w-full md:items-center md:gap-4">
      {showIcon ? (
        <EmbedSvg
          className="xs:size-10 color-dark-purple md:size-12.5"
          src={getIcon(type) || undefined}
        />
      ) : null}
      {content}
    </div>
  );
}

function DetailView({
  component,
  isEditable,
  onEdit,
  onDelete,
  onClose,
  showIcon,
  isViewDetails,
  program,
}: Omit<ProgramComponentHeaderProps, "view">) {
  const { name, type, status } = component || {};
  const statusView = <ProgramComponentStatus component={component} />;
  const isGroupMeeting = component?.type === ComponentTypeEnum.GroupMeeting;
  const isLead = isLeadComponent(component as LeadComponent);
  const allowsGrantExtension = canGrantExtension(
    component,
    isLead,
    program?.status,
  );
  const hasAction = isEditable || allowsGrantExtension;
  const [activeDialog, setActiveDialog] = useState<ComponentDialog>();

  function handleGrantExtension() {
    setActiveDialog("grant-extension");
  }

  const closeDialog = () => {
    setActiveDialog(undefined);
  };

  return (
    <div className="flex">
      <ComponentHeaderTitle
        showIcon={showIcon}
        component={component}
        name={name || ""}
        type={type}
        statusView={statusView}
        isViewDetails={isViewDetails}
      />
      {hasAction && (
        <ComponentContextButton
          component={component as BaseProgramComponent}
          onEdit={onEdit}
          onDelete={onDelete}
          onClose={onClose}
          couldEndEarly={canEndEarlyComponent(status, isGroupMeeting, isLead)}
          canGrantExtension={allowsGrantExtension}
          canEdit={isEditable}
          onGrantExtension={handleGrantExtension}
        />
      )}
      {component && (
        <GrantExtensionDialogs
          open={activeDialog === "grant-extension"}
          onClose={closeDialog}
          component={component as LeadComponent}
        />
      )}
    </div>
  );
}

function ListView({
  component,
  isEditable,
  onEdit,
  onDelete,
  onClose,
  showIcon,
  isViewDetails,
  isParticipant,
  program,
}: Omit<ProgramComponentHeaderProps, "view">) {
  const {
    name,
    type,
    targetStartDate,
    targetDueDate,
    targetEndDate,
    closedAt,
    participantSubmitAt,
    status,
  } = component || {};
  const statusView = <ProgramComponentStatus component={component} />;
  const isGroupMeeting = component?.type === ComponentTypeEnum.GroupMeeting;
  const isLead = isLeadComponent(component as LeadComponent);
  const allowsGrantExtension = canGrantExtension(
    component,
    isLead,
    program?.status,
  );
  const hasAction = isEditable || allowsGrantExtension;
  const [selectedComponent, setSelectedComponent] = useState<LeadComponent>();
  const [activeDialog, setActiveDialog] = useState<ComponentDialog>();

  function handleGrantExtension(component: ProgramComponentType) {
    setSelectedComponent(component as LeadComponent);
    setActiveDialog("grant-extension");
  }

  const closeDialog = () => {
    setActiveDialog(undefined);
    setSelectedComponent(undefined);
  };

  const dateView = (
    <ProgramComponentDateInfo
      targetStartDate={targetStartDate}
      targetDueDate={targetDueDate}
      targetEndDate={targetEndDate}
      closedAt={closedAt}
      participantSubmitAt={participantSubmitAt}
      status={status}
      type={type}
      view={isParticipant ? "participant" : "lead"}
    />
  );

  return (
    <div className="flex">
      <ComponentHeaderTitle
        showIcon={showIcon}
        component={component}
        name={name || ""}
        type={type}
        statusView={statusView}
        dateView={dateView}
        isViewDetails={isViewDetails}
      />
      <div className="flex xs:gap-3.5 min-w-0 flex-none items-center md:gap-4">
        <div className="xs:hidden md:block">{statusView}</div>
        {hasAction && (
          <ComponentContextButton
            component={component as BaseProgramComponent}
            onEdit={onEdit}
            onDelete={onDelete}
            onClose={onClose}
            couldEndEarly={canEndEarlyComponent(status, isGroupMeeting, isLead)}
            canGrantExtension={allowsGrantExtension}
            canEdit={isEditable}
            onGrantExtension={handleGrantExtension}
          />
        )}
        {selectedComponent && (
          <GrantExtensionDialogs
            open={activeDialog === "grant-extension"}
            onClose={closeDialog}
            component={selectedComponent}
          />
        )}
      </div>
    </div>
  );
}

export function ProgramComponentHeader({
  component,
  isEditable,
  onEdit,
  onDelete,
  onClose,
  view = "list",
  showIcon = true,
  isParticipant = false,
  program,
}: ProgramComponentHeaderProps) {
  return view === "details" ? (
    <DetailView
      component={component}
      isEditable={isEditable}
      onEdit={onEdit}
      onDelete={onDelete}
      onClose={onClose}
      showIcon={showIcon}
      isViewDetails={true}
      isParticipant={isParticipant}
      program={program}
    />
  ) : (
    <ListView
      component={component}
      isEditable={isEditable}
      onEdit={onEdit}
      onDelete={onDelete}
      onClose={onClose}
      showIcon={showIcon}
      isParticipant={isParticipant}
      isViewDetails={false}
      program={program}
    />
  );
}
